declare global {
  interface Window {
    cv: any;
  }
}

export default class FindDieLocation {
  maskPattern: any = null;
  maskPolygon: any[] = [];
  maskLT: [number, number] = [0, 0];
  dieNameList: string[] = [];
  state: string = "ready";

  quadraticFormula(a: number, b: number, c: number) {
    return (-b + Math.sqrt(b * b - 4 * a * c)) / (2 * a);
  }

  // ============ 다이 검출 (CalDiePoly) ============
  CalDiePoly(
    img: any,
    rangeColor: [number, number] = [80, 255],
    rateLimit = 0.95,
    dieLimit = 0.005
  ) {
    const cv = window.cv;
    let gray = new cv.Mat();
    if (img.channels() > 1) {
      cv.cvtColor(img, gray, cv.COLOR_BGR2GRAY);
    } else {
      gray = img.clone();
    }
    const h = gray.rows,
      w = gray.cols;
    const areaTotal = h * w;
    const pad = -Math.floor(
      this.quadraticFormula(1, w + h, w * h * (1 - rateLimit))
    );

    const lower = new cv.Mat(h, w, cv.CV_8UC1, new cv.Scalar(rangeColor[0]));
    const upper = new cv.Mat(h, w, cv.CV_8UC1, new cv.Scalar(rangeColor[1]));
    const target = new cv.Mat();
    cv.inRange(gray, lower, upper, target);

    const contours = new cv.MatVector();
    const hierarchy = new cv.Mat();
    cv.findContours(
      target,
      contours,
      hierarchy,
      cv.RETR_EXTERNAL,
      cv.CHAIN_APPROX_SIMPLE
    );

    const contoursTarget: Array<any[]> = [];
    for (let i = 0; i < contours.size(); i++) {
      const cnt = contours.get(i);
      const hull = new cv.Mat();
      cv.convexHull(cnt, hull, true, true);

      const peri = cv.arcLength(hull, true);
      const approx = new cv.Mat();
      cv.approxPolyDP(hull, approx, 0.1 * peri, true);

      const area = cv.contourArea(approx);
      if (approx.rows === 4 && area / areaTotal > dieLimit) {
        const pts: [number, number][] = [];
        for (let r = 0; r < approx.rows; r++) {
          const p = approx.intPtr(r, 0);
          pts.push([p[0], p[1]]);
        }

        // 직각성 검사
        const cosList: number[] = [];
        for (let k = 0; k < 4; k++) {
          const i1 = (k + 3) % 4;
          const v1: [number, number] = [
            pts[i1][0] - pts[k][0],
            pts[i1][1] - pts[k][1],
          ];
          const i2 = (k + 1) % 4;
          const v2: [number, number] = [
            pts[k][0] - pts[i2][0],
            pts[k][1] - pts[i2][1],
          ];
          const denom = Math.hypot(...v1) * Math.hypot(...v2) || 1e-9;
          cosList.push((v1[0] * v2[0] + v1[1] * v2[1]) / denom);
        }

        if (cosList.reduce((s, x) => s + Math.abs(x), 0) / 4 < 0.1) {
          const xs = pts.map((p) => p[0]);
          const ys = pts.map((p) => p[1]);
          const minX = Math.min(...xs),
            maxX = Math.max(...xs);
          const minY = Math.min(...ys),
            maxY = Math.max(...ys);

          if (maxX < w - pad && pad < minX && maxY < h - pad && pad < minY) {
            const rect = new cv.Rect(
              minX,
              minY,
              Math.max(1, maxX - minX),
              Math.max(1, maxY - minY)
            );
            const roi = target.roi(rect);
            const m = cv.mean(roi)[0] / 255.0;
            roi.delete();
            if (m > 0.7) {
              const normPts = pts.map((p) => ({ x: p[0] / w, y: p[1] / h }));
              contoursTarget.push(normPts);
            }
          }
        }
      }
      hull.delete();
      approx.delete();
      cnt.delete();
    }
    contours.delete();
    hierarchy.delete();
    target.delete();
    gray.delete();
    return contoursTarget;
  }

  // ============ 마스크 패턴 생성 ============
  MakeDieMaskPattern(
    images: any[],
    thresholdCenter = 80,
    thresholdWidth = 20,
    rateLimit = 0.95,
    dieLimit = 0.005
  ) {
    this.state = "Make die pattern";
    const cv = window.cv;
    let thresholdList: number[] = [];
    for (
      let t = thresholdCenter - thresholdWidth / 2;
      t <= thresholdCenter + thresholdWidth / 2;
      t += thresholdWidth / 8
    ) {
      if (t >= 0 && t < 256) thresholdList.push(Math.round(t));
    }
    thresholdList = Array.from(new Set(thresholdList));

    const polygonDic: Record<number, any[]> = {};
    thresholdList.forEach((t) => (polygonDic[t] = []));

    for (let th of thresholdList) {
      for (let img of images) {
        const polys = this.CalDiePoly(img, [th, 255], rateLimit, dieLimit);
        polygonDic[th].push(polys);
      }
    }

    // 다이 개수 최빈값
    const numList: number[] = [];
    for (let th in polygonDic) {
      polygonDic[th].forEach((polys) => numList.push(polys.length));
    }
    const freq: Record<number, number> = {};
    numList.forEach((n) => (freq[n] = (freq[n] || 0) + 1));
    const numDie = +Object.entries(freq).reduce((a, b) =>
      a[1] > b[1] ? a : b
    )[0];

    // numDie 개수와 일치하는 샘플만
    const polygonListSample: any[] = [];
    for (let th in polygonDic) {
      for (let polys of polygonDic[th]) {
        if (polys.length === numDie) polygonListSample.push(polys);
      }
    }

    const maskSum = new cv.Mat.zeros(1000, 1000, cv.CV_8UC1);
    for (let polys of polygonListSample.slice(0, 100)) {
      for (let poly of polys) {
        const pts = poly.map((p: { x: number; y: number }) => ({
          x: p.x * 1000,
          y: p.y * 1000,
        }));
        const points = cv.matFromArray(
          pts.length,
          1,
          cv.CV_32SC2,
          pts.flatMap((p: { x: number; y: number }) => [p.x, p.y])
        );
        const contours = new cv.MatVector();
        contours.push_back(points);

        cv.fillPoly(maskSum, contours, new cv.Scalar(1));

        points.delete();
        contours.delete();
      }
    }
    const mask = new cv.Mat();
    cv.threshold(maskSum, mask, 0.5, 1, cv.THRESH_BINARY);

    const contours = new cv.MatVector();
    const hierarchy = new cv.Mat();
    cv.findContours(
      mask,
      contours,
      hierarchy,
      cv.RETR_EXTERNAL,
      cv.CHAIN_APPROX_SIMPLE
    );
    this.maskPolygon = [];
    for (let i = 0; i < contours.size(); i++) {
      const poly = new cv.Mat();
      cv.convexHull(contours.get(i), poly, true, true);
      this.maskPolygon.push(poly);
    }

    const ys: number[] = [],
      xs: number[] = [];
    for (let poly of this.maskPolygon) {
      for (let r = 0; r < poly.rows; r++) {
        const p = poly.intPtr(r, 0);
        xs.push(p[0]);
        ys.push(p[1]);
      }
    }
    const minX = Math.min(...xs),
      maxX = Math.max(...xs);
    const minY = Math.min(...ys),
      maxY = Math.max(...ys);
    this.maskPattern = mask.roi(
      new cv.Rect(minX, minY, maxX - minX, maxY - minY)
    );
    this.maskLT = [minX, minY];

    this.dieNameList = this.maskPolygon.map((poly) => {
      const xs: number[] = [],
        ys: number[] = [];
      for (let r = 0; r < poly.rows; r++) {
        const p = poly.intPtr(r, 0);
        xs.push(p[0]);
        ys.push(p[1]);
      }
      return `${((Math.min(...xs) + Math.max(...xs)) / 2 / 1000).toFixed(3)},${(
        (Math.min(...ys) + Math.max(...ys)) /
        2 /
        1000
      ).toFixed(3)}`;
    });

    this.state = "ready";
  }

  // ============ Export / Import ============
  ExportMaskInfo() {
    return this.maskPolygon.map((poly) => poly.data32S);
  }
  ImportMaskInfo(maskPolygon: any[]) {
    this.maskPolygon = maskPolygon;
  }

  GetDieName() {
    return this.dieNameList;
  }

  // ============ 마스크 적용 및 좌표/이미지 추출 ============
  GetDieMaskFull(data: any, dieNameList: string[] | null = null) {
    const cv = window.cv;
    if (!dieNameList) dieNameList = this.dieNameList;
    const h = data.rows,
      w = data.cols;
    const dataRe = new cv.Mat();
    cv.resize(data, dataRe, new cv.Size(1000, 1000));
    const res = new cv.Mat();
    cv.matchTemplate(dataRe, this.maskPattern, res, cv.TM_CCOEFF_NORMED);
    const result = cv.minMaxLoc(res);
    const LTShift = result.maxLoc;
    const offset: [number, number] = [
      this.maskLT[0] - LTShift.x,
      this.maskLT[1] - LTShift.y,
    ];

    const maskFull = new cv.Mat.zeros(h, w, cv.CV_8UC1);
    for (let poly of this.maskPolygon) {
      const pts: any[] = [];
      for (let r = 0; r < poly.rows; r++) {
        const p = poly.intPtr(r, 0);
        pts.push(
          new cv.Point(
            ((p[0] - offset[0]) / 1000) * w,
            ((p[1] - offset[1]) / 1000) * h
          )
        );
      }
      cv.fillPoly(maskFull, [pts], new cv.Scalar(1));
    }
    this.state = "ready";
    return maskFull;
  }

  GetDieRects(data: any, expand = 0.1, dieNameList: string[] | null = null) {
    if (!dieNameList) dieNameList = this.dieNameList;
    const cv = window.cv;
    const h = data.rows,
      w = data.cols;
    const dataRe = new cv.Mat();
    cv.resize(data, dataRe, new cv.Size(1000, 1000));
    const res = new cv.Mat();
    cv.matchTemplate(dataRe, this.maskPattern, res, cv.TM_CCOEFF_NORMED);
    const LTShift = cv.minMaxLoc(res).maxLoc;
    const offset: [number, number] = [
      this.maskLT[0] - LTShift.x,
      this.maskLT[1] - LTShift.y,
    ];

    const diePackList: Array<[string, [number, number, number, number]]> = [];
    for (let poly of this.maskPolygon) {
      const xs: number[] = [],
        ys: number[] = [];
      for (let r = 0; r < poly.rows; r++) {
        const p = poly.intPtr(r, 0);
        xs.push(p[0] - offset[0]);
        ys.push(p[1] - offset[1]);
      }
      const xmin = Math.min(...xs),
        xmax = Math.max(...xs);
      const ymin = Math.min(...ys),
        ymax = Math.max(...ys);
      const rect: [number, number, number, number] = [
        (xmin / 1000) * w,
        (ymin / 1000) * h,
        ((xmax - xmin) / 1000) * w,
        ((ymax - ymin) / 1000) * h,
      ];
      const dieName = `${((xmin + xmax) / 2 / 1000).toFixed(3)},${(
        (ymin + ymax) /
        2 /
        1000
      ).toFixed(3)}`;
      if (dieNameList.includes(dieName)) diePackList.push([dieName, rect]);
    }
    this.state = "ready";
    return diePackList;
  }

  GetDiePolygons(data: any, dieNameList: string[] | null = null) {
    if (!dieNameList) dieNameList = this.dieNameList;
    const cv = window.cv;
    const h = data.rows,
      w = data.cols;
    const dataRe = new cv.Mat();
    cv.resize(data, dataRe, new cv.Size(1000, 1000));
    const res = new cv.Mat();
    cv.matchTemplate(dataRe, this.maskPattern, res, cv.TM_CCOEFF_NORMED);
    const LTShift = cv.minMaxLoc(res).maxLoc;
    const offset: [number, number] = [
      this.maskLT[0] - LTShift.x,
      this.maskLT[1] - LTShift.y,
    ];

    const diePackList: Record<string, any[]> = {};
    for (let poly of this.maskPolygon) {
      const pts: any[] = [];
      for (let r = 0; r < poly.rows; r++) {
        const p = poly.intPtr(r, 0);
        pts.push(
          new cv.Point(
            ((p[0] - offset[0]) / 1000) * w,
            ((p[1] - offset[1]) / 1000) * h
          )
        );
      }
      const xs = pts.map((p) => p.x),
        ys = pts.map((p) => p.y);
      const dieName = `${((Math.min(...xs) + Math.max(...xs)) / 2 / w).toFixed(
        3
      )},${((Math.min(...ys) + Math.max(...ys)) / 2 / h).toFixed(3)}`;
      if (dieNameList.includes(dieName)) diePackList[dieName] = pts;
    }
    this.state = "ready";
    return diePackList;
  }
}
